import { BUTTON_SIZE, BUTTON_TYPE, COLORS, DSButton } from '@repo/design-system/index'
import classNames from 'classnames'
import { useRef } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import Slider from 'react-slick'
import { GTM_EVENT, sendGTMEvent } from '../../../../helpers/gtmHelper'
import apis from '../../../../modules/apis'
import { axiosPost } from '../../../../modules/axiosHelper'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import LinkLocale from '../../../../modules/i18n/LinkLocale'
import { LOCALE_KO } from '../../../../modules/i18n/config'
import { s_home } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import urls from '../../../../modules/urls'
import { CMSMobileSection } from '../../../new-portal/pricing/CMSSection'
import {
  IMG_BUSINESS_CHECK_ICON,
  IMG_BUSINESS_COMPANY_1,
  IMG_BUSINESS_COMPANY_2,
  IMG_BUSINESS_COMPANY_3,
  IMG_BUSINESS_COMPANY_4,
  IMG_BUSINESS_COMPANY_5,
  IMG_BUSINESS_SYSTEM_IMG_MOBILE,
} from '../../constants/business.constants'
import {
  IMG_HOME_DESKTOP_ICON_CHEVRON_CIRCLE_LEFT,
  IMG_HOME_DESKTOP_ICON_CHEVRON_CIRCLE_RIGHT,
  IMG_HOME_SECTION_ICON_REVIEW_V2,
} from '../../constants/home.constants'
import { BUSINESS_REVIEW_LIST } from '../../data/business.data'
import { IInquiryFormData } from '../../interface/business.interface'

const BusinessBody: React.FC = () => {
  const { t, locale } = useTranslation()
  const appRouter = useAppRouter()

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useForm<IInquiryFormData>({ mode: 'onTouched' })

  const sliderRef = useRef<Slider>()
  const inquirySectionRef = useRef<HTMLDivElement>(null)

  const goToReviewPage = () => {
    appRouter.push(urls.student.landing.blog.tags('링글 후기'))
  }

  const handleInquiryForm: SubmitHandler<IInquiryFormData> = async (data) => {
    const response = await axiosPost(apis.landing.partner(), {
      name: data.name,
      email: data.email,
      content: data.inquiryText,
      phone: data.mobileNumber,
      company_category: data.companyCategory,
    })

    if (response.success) {
      sendGTMEvent(GTM_EVENT.SUBMIT_APPLICATOIN)

      alert(t(['제휴문의가 성공적으로 제출 되었습니다!', 'Your inquiry has been successfully submitted.']))
      reset()
    } else {
      alert(
        t([
          '제휴문의 제출이 실패하였습니다. 에러가 반복 될 시 링글 헬프센터로 연락 부탁 드립니다.',
          'There has been an error while submitting your inquiry. If this problem persists, Please contact the Ringle Help Center for further assistance.',
        ])
      )
    }
  }

  return (
    <div>
      {/* Section 1 */}
      <div className="px-18 w-full pt-[32px] pb-[41px]">
        <div className="flex flex-col items-center justify-center space-y-20">
          <img src={IMG_BUSINESS_COMPANY_1} alt="company-row-one" />
          <img src={IMG_BUSINESS_COMPANY_2} alt="company-row-two" />
          <img src={IMG_BUSINESS_COMPANY_3} alt="company-row-three" />
          <img src={IMG_BUSINESS_COMPANY_4} alt="company-row-four" />
          <img src={IMG_BUSINESS_COMPANY_5} alt="company-row-five" />
        </div>

        <div className="mt-[34px] text-center">
          <h1
            className={classNames(
              'whitespace-pre-line text-purple-500',
              locale === LOCALE_KO ? 'Headline2-24Bd' : 'Headline3-22Bd'
            )}
          >
            {t(s_home.business.title.mobile)}
          </h1>
          <p
            className={classNames(
              'mt-12 whitespace-pre-line text-[#585D71]',
              locale === LOCALE_KO ? 'Caption-12Md' : 'Overline-10Md'
            )}
          >
            {t(s_home.business.subtitle.mobile)}
          </p>

          <button
            onClick={() => inquirySectionRef?.current?.scrollIntoView()}
            className={classNames(
              'mt-20 min-w-[186px] rounded-[7px] bg-[#4130A4] px-12 py-12 text-white',
              locale === LOCALE_KO ? 'Body2-14Bd' : 'Caption-12Bd'
            )}
          >
            {t(s_home.business.cta.mobile)}
          </button>
        </div>
      </div>

      {/* Section 2 */}
      <div className="flex w-full items-center justify-center bg-[#F8F8FB]">
        <div className="w-full pt-[68px] pb-[47px]">
          {/* Label Section */}
          <div className="flex items-center justify-center">
            <img src={IMG_HOME_SECTION_ICON_REVIEW_V2} alt="b2b-review-icon" className="h-[24px] w-[24px]" />
            <p className={classNames('ml-4 text-[#585D71]', locale === LOCALE_KO ? 'Title2-18Bd' : 'Body1-16Bd')}>
              {t(s_home.business_review.subtitle.mobile)}
            </p>
          </div>

          <h2
            className={classNames(
              'mt-12 whitespace-pre-line text-center text-black',
              locale === LOCALE_KO ? 'Headline2-24Bd' : 'Headline3-22Bd'
            )}
          >
            {t(s_home.business_review.title.mobile)}
          </h2>

          <div className="flex justify-center pt-[32px]">
            <DSButton
              type={BUTTON_TYPE.OUTLINE}
              size={BUTTON_SIZE.MD}
              className="max-w-[186px]"
              customBorderColor={COLORS.PURPLE}
              onClick={goToReviewPage}
            >
              <DSButton.Label customTxtColor={COLORS.PURPLE}>{t(['후기 더 알아보기', 'more reviews'])}</DSButton.Label>
            </DSButton>
          </div>

          <div className="pl-[12px] mt-[32px]">
            <Slider
              ref={sliderRef}
              className="slider"
              slidesToShow={1}
              slidesToScroll={1}
              prevArrow={<></>}
              nextArrow={<></>}
              variableWidth={true}
            >
              {BUSINESS_REVIEW_LIST(locale, true).map((item) => (
                <div key={item.id} className="!w-[270px] py-6">
                  <div className="mx-[8px] rounded-xl bg-white px-[20px] pt-[24px] pb-[32px] shadow-[0px_2px_8px_rgba(0,0,0,0.15)]">
                    <img src={item.logoUrl} alt="logo-url" className="h-[43px]" />
                    <p
                      className={classNames(
                        'mt-[8px] break-words break-keep',
                        locale === LOCALE_KO ? 'Body2-14Md' : 'Caption-12Md'
                      )}
                    >
                      {item.reviewText}
                    </p>
                  </div>
                </div>
              ))}
            </Slider>

            <div className="mt-[20px] flex w-full items-center justify-center space-x-[24px]">
              <button onClick={() => sliderRef?.current?.slickPrev()}>
                <img src={IMG_HOME_DESKTOP_ICON_CHEVRON_CIRCLE_LEFT} alt="chevron-left" className="h-[32px] w-[32px]" />
              </button>
              <button onClick={() => sliderRef?.current?.slickNext()}>
                <img
                  src={IMG_HOME_DESKTOP_ICON_CHEVRON_CIRCLE_RIGHT}
                  alt="chevron-right"
                  className="h-[32px] w-[32px]"
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Section 3 */}

      {/* Section 4 */}
      <div className="w-full px-20 pt-[68px] pb-[44px]">
        {/* Label Section */}
        <div className="flex items-center justify-center">
          <img src={IMG_BUSINESS_CHECK_ICON} alt="b2b-service-icon" className="h-[24px] w-[24px]" />
          <p className={classNames('ml-4 text-[#585D71]', locale === LOCALE_KO ? 'Title2-18Bd' : 'Body1-16Bd')}>
            {t(s_home.business_manage.subtitle.mobile)}
          </p>
        </div>

        <h2
          className={classNames(
            'mt-16 whitespace-pre-line text-center text-black',
            locale === LOCALE_KO ? 'Headline2-24Bd' : 'Headline3-22Bd'
          )}
        >
          {t(s_home.business_manage.title.mobile)}
        </h2>

        <div className="mt-[36px] flex flex-col items-start justify-between rounded-xl bg-[#31247B] pt-[32px] pl-[20px] shadow-[0px_2px_8px_rgba(0,0,0,0.15)]">
          <div>
            <p
              className={classNames(
                'whitespace-pre-line text-white',
                locale === LOCALE_KO ? 'Headline3-22Bd' : 'Title1-20Bd'
              )}
            >
              {t(s_home.business_manage.system_title.mobile)}
            </p>
            <p
              className={classNames(
                'mt-[20px] whitespace-pre-line text-[#F8F8FB]',
                locale === LOCALE_KO ? 'Caption-12Md' : 'Overline-10Md'
              )}
            >
              {t(s_home.business_manage.system_bodytxt_1.mobile)}
            </p>
            <p
              className={classNames(
                'mt-[12px] whitespace-pre-line text-[#F8F8FB]',
                locale === LOCALE_KO ? 'Caption-12Md' : 'Overline-10Md'
              )}
            >
              {t(s_home.business_manage.system_bodytxt_2.mobile)}
            </p>
          </div>

          <img src={IMG_BUSINESS_SYSTEM_IMG_MOBILE} alt="system-img-mobile" className="mt-[25px] rounded-br-xl" />
        </div>
      </div>

      {/* CMS Section */}
      <CMSMobileSection slug={'b2b'} />

      {locale === LOCALE_KO && (
        <div className="flex w-full items-center justify-center bg-[#201852]">
          <div className="w-full max-w-[1080px] px-20 py-[32px] text-center">
            <div className="mt-16 whitespace-pre-line text-center text-[18px] font-bold text-white">
              {t(s_home.business_export.subtitle.mobile)}
            </div>
            <div className="mt-[16px] w-full">
              <LinkLocale href={urls.student.landing.voucher}>
                <button
                  className={classNames('Body2-14Bd min-w-[186px] rounded-[7px] bg-[#4130A4] px-12 py-12 text-white')}
                >
                  {t(s_home.business_export.cta.mobile)}
                </button>
              </LinkLocale>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BusinessBody
